import { useSubscription } from "@apollo/client";
import { Card, Col, Row, Statistic, Typography } from "antd";
import { useEffect, useState } from "react";

import { formatDate } from "@src/utils/dateHelpers";

import { EdgeStateDocument } from "../../graphql/graphql";
import { OpcuaState } from "../../graphql/graphql";

const { Text } = Typography;

/**
 * Displays the OPC UA health of an asset.
 *
 * This includes the OPC UA connection status, the last check time, the network connection status,
 * the session establishment status and the netcat log.
 */
const OpcuaHealth = ({ assetId }: { assetId: string }) => {
  const [opcuaState, setOpcuaState] = useState<OpcuaState>({
    connected: false,
    lastCheckAt: null,
    netcatLog: "",
    networkConnectionPossible: false,
    sessionEstablished: false,
  });

  const { data, error } = useSubscription(EdgeStateDocument, {
    variables: { assetId },
  });

  useEffect(() => {
    if (data?.edgeState.opcuaState) {
      setOpcuaState(data.edgeState.opcuaState);
    }
  }, [data]);

  if (error) {
    return (
      <Text
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20px",
          alignItems: "center",
          height: "100%",
        }}
      >
        Error fetching OPC UA state: {error.message}
      </Text>
    );
  }

  return (
    <Row gutter={16}>
      <Col span={6}>
        <Card bordered={false}>
          <Statistic
            title="Last Check At"
            value={
              opcuaState.lastCheckAt
                ? formatDate(opcuaState.lastCheckAt)
                : "N/A"
            }
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card bordered={false}>
          <Statistic
            title="OPC-UA Connected"
            value={opcuaState.connected ? "Connected" : "Not connected"}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card bordered={false}>
          <Statistic
            title="Session Established"
            value={
              opcuaState.sessionEstablished
                ? "Session Established"
                : "No Session Established"
            }
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card bordered={false}>
          <Statistic
            title="Network Connection Possible"
            value={
              opcuaState.networkConnectionPossible
                ? "Network connection"
                : "No Network Connection"
            }
          />
        </Card>
      </Col>
      <Col span={24} style={{ marginTop: 16 }}>
        <Card bordered={false}>
          <Statistic
            title="Netcat Log"
            value={
              opcuaState.netcatLog ? opcuaState.netcatLog : "No logs available"
            }
            valueStyle={{
              fontSize: "14px",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            }}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default OpcuaHealth;
